import { HeapPHIDecorator } from "@app/components/analytics";
import React, { FC, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";

import { GridCell, GridRow } from "@natera/material/lib/layout";
import { Link } from "@natera/navigation";
import { ConfigContext, ProfileContext, UserContext } from "@app/provider";

import LinkArrowIcon from "@assets/svg/icons/link-arrow.svg";
import ContactIcon from "@assets/svg/icons/contact.svg";
import MessageIcon from "@assets/svg/icons/message.svg";
import AccountIcon from "@assets/svg/icons/account.svg";
import LockIcon from "@assets/svg/icons/lock.svg";
import { routes } from "@app/routing";

import ProfileDeleteDialog from "./profileDeleteDialog";
import { useDialog } from "@natera/platform/lib/hooks";
import { Button } from "@natera/form";
import { DetailInformationButton, SpinnerView } from "@app/components";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { getFormattedToUTCDate } from "@app/utils";

import "./profile.scss";

const messages = defineMessages({
  profileTitle: {
    id: "profileTitle",
    defaultMessage: "Account",
  },
  profileBirthDay: {
    id: "profileBirthDay",
    defaultMessage: "Date of Birth: {birthday}",
  },
  profileDetailsLink: {
    id: "profileDetailsLink",
    defaultMessage: "Personal Information",
  },
  profileContactLink: {
    id: "profileContactLink",
    defaultMessage: "Contact Details",
  },
  profileSecurityLink: {
    id: "profileSecurityLink",
    defaultMessage: "Security Settings",
  },
  profileCommunicationLink: {
    id: "profileCommunicationLink",
    defaultMessage: "Communication Preferences",
  },
  profileTerms: {
    id: "profileTerms",
    defaultMessage: "Terms of Use",
  },
  profilePrivacyPolicy: {
    id: "profilePrivacyPolicy",
    defaultMessage: "Privacy Policy",
  },
  profileLicenses: {
    id: "profileLicenses",
    defaultMessage: "Licenses",
  },
  profileNoticeOfPrivacyPractices: {
    id: "profileNoticeOfPrivacyPractices",
    defaultMessage: "Notice of Privacy Practices",
  },
  profileDeleteAccount: {
    id: "profileDeleteAccount",
    defaultMessage: "Delete Account",
  },
});

const Profile: FC = () => {
  const intl = useIntl();
  const profileDeleteDialog = useDialog(ProfileDeleteDialog);
  const {
    getProfileData: profile,
    getProfile,
    getProfileIsLoading,
  } = useContext(ProfileContext);
  const { uppUser, isLoading } = useContext(UserContext);
  const { config } = useContext(ConfigContext);

  React.useEffect(() => {
    getProfile();
  }, []);

  if (!profile || !uppUser) {
    return null;
  }

  const { firstName, lastName, dateOfBirth } = profile;
  const { email } = uppUser;

  const handleOpenDeleteDialog = () => {
    profileDeleteDialog.open({ onClose: handleCloseDeleteDialog });
  };

  const handleCloseDeleteDialog = () => {
    profileDeleteDialog.close();
  };

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.profileTitle)}
      backArrowHeaderRoute={routes.home}
      noGradient
    >
      <div className="profile">
        <SpinnerView isLoading={getProfileIsLoading || isLoading} />
        {profileDeleteDialog.getDialog()}
        <GridRow className="profile__patient">
          <GridCell span={12}>
            <div className="patient-card">
              <HeapPHIDecorator protectText>
                <div className="patient-card__name">
                  {`${firstName?.[0]}${lastName?.[0]}`}
                </div>
              </HeapPHIDecorator>

              <div className="patient-card__info">
                <HeapPHIDecorator protectText inline>
                  <h3>
                    {firstName} {lastName}
                  </h3>
                </HeapPHIDecorator>

                <div className="patient-card__info-wrp">
                  <HeapPHIDecorator protectText inline>
                    <p>
                      {email}
                      <span className="patient-card__info-bullet">&#8226;</span>
                    </p>
                  </HeapPHIDecorator>
                  <HeapPHIDecorator protectText inline>
                    <p>
                      {intl.formatMessage(messages.profileBirthDay, {
                        birthday: getFormattedToUTCDate(dateOfBirth),
                      })}
                    </p>
                  </HeapPHIDecorator>
                </div>
              </div>
            </div>
          </GridCell>
        </GridRow>

        <GridRow className="profile__routes">
          <GridCell className="profile__routes__link" span={12}>
            <DetailInformationButton
              route={routes.personalInformation}
              name={intl.formatMessage(messages.profileDetailsLink)}
              startIcon={AccountIcon}
              endIcon={LinkArrowIcon}
            />
          </GridCell>

          <GridCell className="profile__routes__link" span={12}>
            <DetailInformationButton
              route={routes.contactDetails}
              name={intl.formatMessage(messages.profileContactLink)}
              startIcon={ContactIcon}
              endIcon={LinkArrowIcon}
            />
          </GridCell>

          <GridCell className="profile__routes__link" span={12}>
            <DetailInformationButton
              route={routes.securitySettings}
              name={intl.formatMessage(messages.profileSecurityLink)}
              startIcon={LockIcon}
              endIcon={LinkArrowIcon}
            />
          </GridCell>

          <GridCell className="profile__routes__link" span={12}>
            <DetailInformationButton
              route={routes.communicationPreferences}
              name={intl.formatMessage(messages.profileCommunicationLink)}
              startIcon={MessageIcon}
              endIcon={LinkArrowIcon}
            />
          </GridCell>
        </GridRow>

        <div className="profile__separator" />

        <GridRow className="profile__footer">
          <GridCell span={12}>
            <Link to={config.links.TERMS} target="_blank">
              {intl.formatMessage(messages.profileTerms)}
            </Link>
          </GridCell>

          <GridCell span={12}>
            <Link to={config.links.PRIVACY_POLICY} target="_blank">
              {intl.formatMessage(messages.profilePrivacyPolicy)}
            </Link>
          </GridCell>

          <GridCell span={12}>
            <Link to={config.links.LICENSES} target="_blank">
              {intl.formatMessage(messages.profileLicenses)}
            </Link>
          </GridCell>

          <GridCell span={12}>
            <Link to={config.links.NOTICE_OF_PRIVACY_PRACTICES} target="_blank">
              {intl.formatMessage(messages.profileNoticeOfPrivacyPractices)}
            </Link>
          </GridCell>

          <GridCell span={12}>
            <Button
              disabled
              onClick={handleOpenDeleteDialog}
              className="profile__footer__open-delete-dialog"
            >
              {intl.formatMessage(messages.profileDeleteAccount)}
            </Button>
          </GridCell>
        </GridRow>
      </div>
    </BlueHeaderContent>
  );
};
export default Profile;
